import { AxiosInstance } from "axios"

import { CompaniesUsersItem, User, UserAction } from "../../../../users/userTypes"
import { ServiceResponse } from "../../../../../core/api/apiTypes"

export enum CompanyAdminUsersQuery {
    USERS_FOR_COMPANY = "users-for-company",
    USER_DETAILS = "company-user-details",
}

export type CompaniesUsersParams = Readonly<{
    companyId: number
    userGroupId?: number
    workgroupId?: number
    search?: string
}>

export type CompaniesUsersResponse = Readonly<{
    users: CompaniesUsersItem[]
}>

export async function fetchCompaniesUsers(
    { companyId, userGroupId, workgroupId, search }: CompaniesUsersParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<CompaniesUsersResponse> {
    const url = `/companies/${companyId}/users`
    const { data } = await axios.get(url, {
        params: {
            userGroupId,
            workgroupId,
            search,
        },
        signal,
    })

    return data
}

// --- Company create/update user (new split-settings contract) ---

export type IdRef = Readonly<{ id: number }>

export { UserActionType } from "../../../../users/userTypes"
export type { UserAction }

/**
 * The `user` body sent to POST /users and POST /users/:id. Mirrors the updated Postman contract:
 * user-sync lives under `company.companySetup.userSynchronization`, the email invitation is sent via the
 * sibling `actions` array, and related entities collapse to id-only references.
 */
export type CompanyUserPayload = Readonly<{
    employeeId?: string
    initials?: string
    username?: string
    displayName: string
    email: string
    phoneNumber?: string
    mobileNumber?: string
    language?: string
    roles?: ReadonlyArray<string>
    company?: Readonly<{ companySetup: Readonly<{ userSynchronization: boolean }> }>
    userGroup?: IdRef
    department?: IdRef
    workgroups?: ReadonlyArray<IdRef>
    approverForUserGroups?: ReadonlyArray<IdRef>
    assignedPriceLists?: ReadonlyArray<IdRef>
    notificationSettings?: Readonly<{ notifyOnGuestArrival: boolean }>
    isActive?: boolean
}>

export type SaveCompanyUserBody = Readonly<{
    user: CompanyUserPayload
    actions?: ReadonlyArray<UserAction>
}>

type CompanyUserResponse = ServiceResponse<Readonly<{ user: User }>>

export type CreateCompanyUserParams = SaveCompanyUserBody

export async function createCompanyUser(
    { user, actions }: CreateCompanyUserParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<CompanyUserResponse> {
    const { data } = await axios.post(`/users`, { user, actions }, { signal })

    return data
}

export type UpdateCompanyUserParams = SaveCompanyUserBody & Readonly<{ userId: number }>

export async function updateCompanyUser(
    { userId, user, actions }: UpdateCompanyUserParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<CompanyUserResponse> {
    const { data } = await axios.post(`/users/${userId}`, { user, actions }, { signal })

    return data
}

// --- Company user bulk operations (atomic backend endpoints) ---

export type MoveUsersToUserGroupParams = Readonly<{
    userGroupId: number
    userIds: ReadonlyArray<number>
}>

export async function moveUsersToUserGroup(
    { userGroupId, userIds }: MoveUsersToUserGroupParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse<{}>> {
    const { data } = await axios.post(
        `/users/toUserGroup`,
        { userMoveOperation: { destination: { userGroup: { id: userGroupId } }, userIds } },
        { signal },
    )

    return data
}

export type UpdateUsersStatusParams = Readonly<{
    isActive: boolean
    userIds: ReadonlyArray<number>
}>

export async function updateUsersStatus(
    { isActive, userIds }: UpdateUsersStatusParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse<{}>> {
    const { data } = await axios.post(`/users/status`, { userStatusOperation: { isActive, userIds } }, { signal })

    return data
}

export type SendUsersInviteParams = Readonly<{
    userIds: ReadonlyArray<number>
}>

export async function sendUsersInvite(
    { userIds }: SendUsersInviteParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse<{}>> {
    const { data } = await axios.post(`/users/sendInvite`, { userInviteOperation: { userIds } }, { signal })

    return data
}
